import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, Column, DataTable, EmptyState, SearchInput, Spinner, StatusPill } from '../../../ui'
import { CategoryMeta } from '../commsInboxView/ReplyDetailPane'
import { SentEmailModal } from '../commsInboxView/SentEmailModal'
import { formatDateTime, humanise, sendKindLabel } from '../commsInboxView/shared'
import { ConversationRow } from '../commsMailboxWorkspaceView/ConversationRow'
import { ConversationTrail } from '../commsMailboxWorkspaceView/ConversationTrail'
import { ConversationListItem } from '../commsMailboxWorkspaceView/types'
import wsStyles from '../commsMailboxWorkspaceView/styles.module.scss'
import styles from './styles.module.scss'

interface ClientHit {
    claimRef: string
    introducer: string | null
    lenderName: string | null
    lenderNormalisedName: string | null
    clientFirstName: string | null
    clientLastName: string | null
    clientEmail: string | null
    clientPostcode: string | null
    proclaimReference: string | null
    agreementNumber: string | null
    vehicleRegistration: string | null
}

interface LetterHit {
    id: string
    claimRef: string | null
    clientRef: string | null
    lenderName: string | null
    introducer: string | null
    caseId: string | null
    sendKind: string | null
    toEmail: string | null
    clientEmail: string | null
    subject: string | null
    status: string
    queuedAt: string | null
    sentAt: string | null
    deliveredAt: string | null
    batchId: string | null
    footerRef: string | null
    replyCount: number
    lastReplyAt: string | null
    conversationId: string | null
}

interface SearchResponse {
    success: boolean
    query: { kind: 'empty' | 'email' | 'reference' | 'name'; value: string }
    clients: ClientHit[]
    conversations: ConversationListItem[]
    conversationsTotal: number
    letters: LetterHit[]
    classifications?: { key: string; label: string; description?: string | null; next_action_label?: string | null }[]
}

const KIND_HINT: Record<SearchResponse['query']['kind'], string> = {
    empty: '',
    email: 'Treated as an email address: every thread this address took part in, every client record holding it and every letter sent to or about it.',
    reference: 'Treated as a reference: matched against claim references, Proclaim references, agreement numbers and vehicle registrations.',
    name: 'Treated as a name: matched against client first and last names on our records.',
}

const letterStatusTone = (status: string) => (status === 'delivered' ? 'good' : status === 'bounced' || status === 'failed' || status === 'complained' ? 'bad' : status === 'sent' ? 'info' : 'neutral')

/**
 * One box, every mailbox: type a client email, a case or claim reference, or a
 * name and see the client records, the letters we sent and every conversation
 * that touched them. URL: /comms/search?q=…&conversation=…
 */
export const CommsSearchView = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const initialQ = searchParams.get('q') || ''
    const [draft, setDraft] = useState(initialQ)
    const [q, setQ] = useState(initialQ)
    const [result, setResult] = useState<SearchResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [selected, setSelected] = useState<string | null>(searchParams.get('conversation'))
    const [sentEmailId, setSentEmailId] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const run = useCallback(async (term: string) => {
        const trimmed = term.trim()
        if (!trimmed) {
            setResult(null)
            return
        }
        setLoading(true)
        try {
            const res = await api({ url: 'comms/inbox/search', data: { q: trimmed } })
            if (res.data?.success) setResult(res.data)
        } catch (err: any) {
            Report.failure('Search failed', err?.response?.data?.message || 'Could not run the search.', 'Okay')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        run(q)
    }, [q, run])

    useEffect(() => {
        const next = new URLSearchParams()
        if (q) next.set('q', q)
        if (selected) next.set('conversation', selected)
        if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true })
    }, [q, selected, searchParams, setSearchParams])

    useEffect(() => {
        if (!initialQ) inputRef.current?.focus()
    }, [initialQ])

    const submit = (e: FormEvent) => {
        e.preventDefault()
        setSelected(null)
        setQ(draft)
    }

    const categoryMeta = useMemo(() => {
        const map = new Map<string, CategoryMeta>()
        for (const c of result?.classifications ?? []) map.set(c.key, { key: c.key, label: c.label, description: c.description ?? null, nextActionLabel: c.next_action_label ?? null })
        return map
    }, [result])
    const categoryLabel = (key: string | null) => (key ? categoryMeta.get(key)?.label ?? humanise(key) : 'Not yet categorised')

    const clientColumns: Column<ClientHit>[] = [
        { key: 'client', header: 'Client', render: (c) => `${c.clientFirstName ?? ''} ${c.clientLastName ?? ''}`.trim() || '—' },
        { key: 'email', header: 'Email', render: (c) => <span className={wsStyles.mono}>{c.clientEmail ?? '—'}</span> },
        { key: 'claim', header: 'Claim ref', render: (c) => <span className={wsStyles.mono}>{c.claimRef}</span> },
        { key: 'lender', header: 'Lender', render: (c) => c.lenderName ?? '—' },
        { key: 'introducer', header: 'Introducer', render: (c) => c.introducer ?? '—' },
        { key: 'agreement', header: 'Agreement', render: (c) => <span className={wsStyles.mono}>{c.agreementNumber ?? '—'}</span> },
        { key: 'reg', header: 'Vehicle', render: (c) => <span className={wsStyles.mono}>{c.vehicleRegistration ?? '—'}</span> },
        { key: 'proclaim', header: 'Proclaim', render: (c) => <span className={wsStyles.mono}>{c.proclaimReference ?? '—'}</span> },
    ]

    const letterColumns: Column<LetterHit>[] = [
        { key: 'sent', header: 'Sent', render: (l) => formatDateTime(l.sentAt ?? l.queuedAt) },
        { key: 'to', header: 'To', render: (l) => <span className={wsStyles.mono}>{l.toEmail ?? '—'}</span> },
        { key: 'lender', header: 'Lender', render: (l) => l.lenderName ?? '—' },
        { key: 'claim', header: 'Claim ref', render: (l) => <span className={wsStyles.mono}>{l.claimRef ?? '—'}</span> },
        { key: 'kind', header: 'Letter type', render: (l) => sendKindLabel(l.sendKind ?? 'initial_loc') },
        {
            key: 'status',
            header: 'Status',
            render: (l) => (
                <StatusPill size="sm" tone={letterStatusTone(l.status)} dot>
                    {humanise(l.status)}
                </StatusPill>
            ),
        },
        { key: 'replies', header: 'Replies', align: 'right', render: (l) => (l.replyCount ? `${l.replyCount} linked to this letter · last ${formatDateTime(l.lastReplyAt)}` : 'none linked to this letter') },
        {
            key: 'actions',
            header: '',
            align: 'right',
            render: (l) => (
                <span className={styles.rowActions}>
                    <Button size="sm" variant="link" onClick={() => setSentEmailId(l.id)}>
                        Open letter
                    </Button>
                    {l.conversationId && (
                        <Button size="sm" variant="link" onClick={() => setSelected(l.conversationId)}>
                            Thread
                        </Button>
                    )}
                </span>
            ),
        },
    ]

    const mailboxesHit = useMemo(() => {
        const m = new Map<string, number>()
        for (const c of result?.conversations ?? []) if (c.mailbox) m.set(c.mailbox, (m.get(c.mailbox) ?? 0) + 1)
        return Array.from(m.entries()).sort(([, a], [, b]) => b - a)
    }, [result])

    return (
        <div className={wsStyles.stack}>
            <form onSubmit={submit} className={styles.searchRow}>
                <SearchInput ref={inputRef} value={draft} onChange={setDraft} placeholder="Client email, claim or case reference, Proclaim reference, agreement number, vehicle registration or name…" className={styles.searchBox} />
                <Button type="submit" disabled={loading || !draft.trim()}>
                    Search
                </Button>
            </form>

            {!q && (
                <EmptyState title="Search across every mailbox" description="Type a client's email address to see everything they were part of, a claim or case reference to see the letters we sent and the replies that came back, or a name to find the client first." />
            )}

            {loading && !result && <Spinner block text="Searching…" />}

            {result && (
                <>
                    <Banner tone="info">
                        <strong>{result.query.kind === 'email' ? 'Email' : result.query.kind === 'reference' ? 'Reference' : 'Name'}:</strong> <span className={wsStyles.mono}>{result.query.value}</span> — {KIND_HINT[result.query.kind]}
                    </Banner>

                    <Card as="section" padding="sm" title="Client records" subtitle={`${result.clients.length} match${result.clients.length === 1 ? '' : 'es'} in our comms identity records`}>
                        {result.clients.length === 0 ? (
                            <p className={wsStyles.note}>No client record matches. If this is a client you expect to find, their letter may not have been sent yet or the details differ from what was entered.</p>
                        ) : (
                            <DataTable<ClientHit>
                                columns={clientColumns}
                                rows={result.clients}
                                rowKey={(c) => `${c.claimRef}-${c.clientEmail ?? ''}`}
                                dense
                                onRowClick={(c) => {
                                    setDraft(c.claimRef)
                                    setSelected(null)
                                    setQ(c.claimRef)
                                }}
                            />
                        )}
                    </Card>

                    <Card as="section" padding="sm" title="Letters we sent" subtitle={`${result.letters.length} letter${result.letters.length === 1 ? '' : 's'}`}>
                        {result.letters.length === 0 ? <p className={wsStyles.note}>No letters match.</p> : <DataTable<LetterHit> columns={letterColumns} rows={result.letters} rowKey={(l) => l.id} dense />}
                    </Card>

                    <Card
                        as="section"
                        padding="sm"
                        title="Conversations"
                        subtitle={
                            <>
                                {result.conversationsTotal} thread{result.conversationsTotal === 1 ? '' : 's'}
                                {result.conversationsTotal > result.conversations.length ? ` (showing the ${result.conversations.length} most recent)` : ''}
                                {mailboxesHit.length > 0 && (
                                    <>
                                        {' · '}
                                        {mailboxesHit.map(([mb, n], i) => (
                                            <span key={mb}>
                                                {i > 0 ? ', ' : ''}
                                                <button type="button" className={styles.linkish} onClick={() => navigate(`/comms/mailboxes/${encodeURIComponent(mb)}?tab=received`)}>
                                                    {mb}
                                                </button>{' '}
                                                ({n})
                                            </span>
                                        ))}
                                    </>
                                )}
                            </>
                        }
                    >
                        {result.conversations.length === 0 ? (
                            <p className={wsStyles.note}>No conversations yet. A thread appears here once a letter has been sent for the claim or an email mentioning the client has arrived.</p>
                        ) : (
                            <div className={wsStyles.layout}>
                                <ul className={wsStyles.convList}>
                                    {result.conversations.map((r) => (
                                        <ConversationRow key={r.id} row={r} selected={r.id === selected} onSelect={setSelected} categoryLabel={categoryLabel} showMailbox />
                                    ))}
                                </ul>
                                <div className={wsStyles.detail}>
                                    <ConversationTrail
                                        conversationId={selected}
                                        categoryMeta={categoryMeta}
                                        onChanged={() => run(q)}
                                        onShowAllOnClaim={(ref) => {
                                            setDraft(ref)
                                            setSelected(null)
                                            setQ(ref)
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </Card>
                </>
            )}

            <SentEmailModal sendId={sentEmailId} onClose={() => setSentEmailId(null)} />
        </div>
    )
}
